import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "@/components/Layout";
import SensorHistoryChart from "@/components/SensorHistoryChart";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, getApiErrorMessage } from "@/lib/api";
import {
  Activity,
  Calendar,
  Download,
  Edit2,
  FileSpreadsheet,
  FileText,
  History,
  Loader,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from "lucide-react";
import mqtt from "mqtt";
import { toast } from "sonner";

const MQTT_BROKER_URL =
  process.env.REACT_APP_MQTT_BROKER_URL || "wss://aquatank.aquasense.tech:9001";
const MQTT_USERNAME = process.env.REACT_APP_MQTT_USERNAME || "";
const MQTT_PASSWORD = process.env.REACT_APP_MQTT_PASSWORD || "";
const MQTT_D2C_TOPIC = process.env.REACT_APP_MQTT_D2C_TOPIC || "AQUATANK/D2C/+";
const MQTT_STATE_TOPIC =
  process.env.REACT_APP_MQTT_STATE_TOPIC || "AQUATANK/STATE/+/+";
const ALL_SENSORS_VALUE = "__all_sensors__";
const IST_TIME_ZONE = "Asia/Kolkata";

const normalizeSensorId = (value) => String(value || "").trim().toLowerCase();
const normalizeDeviceId = (value) => String(value || "").trim().toLowerCase();
// Sensor names are unique per society; older sensors saved without a name
// fall back to their Sensor ID and device so options stay distinguishable.
const getHistorySensorLabel = (sensor) => {
  const name = String(sensor?.sensor_name || "").trim();
  return name || `${sensor?.s_id || "-"} (${sensor?.device_id || "-"})`;
};
const getSensorIdFromSelectValue = (value) =>
  !value || value === ALL_SENSORS_VALUE ? "" : value;

const getStateTopicSubscriptions = () => {
  const topics = new Set([MQTT_STATE_TOPIC]);
  const wildcardIndex = MQTT_STATE_TOPIC.search(/[+#]/);
  const baseTopic =
    wildcardIndex >= 0
      ? MQTT_STATE_TOPIC.slice(0, wildcardIndex).replace(/\/+$/, "")
      : MQTT_STATE_TOPIC.replace(/\/+$/, "");

  if (baseTopic) {
    topics.add(baseTopic);
    topics.add(`${baseTopic}/#`);
  }

  return Array.from(topics);
};

const extractPackets = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.packet) return extractPackets(payload.packet);
  if (payload?.payload) return extractPackets(payload.payload);
  if (Array.isArray(payload?.packets)) return payload.packets;
  if (Array.isArray(payload?.data)) return payload.data;
  return payload ? [payload] : [];
};

const getTimestampMs = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp === "number") {
    return timestamp < 100000000000 ? timestamp * 1000 : timestamp;
  }

  const numeric = Number(timestamp);
  if (Number.isFinite(numeric)) {
    return numeric < 100000000000 ? numeric * 1000 : numeric;
  }

  const parsed = new Date(timestamp).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const formatIST = (timestamp) => {
  const timestampMs = getTimestampMs(timestamp);
  if (!timestampMs) return "Waiting for data";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(new Date(timestampMs));
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "No data";
  return String(value);
};

const hasReadingValue = (value) =>
  value !== null && value !== undefined && value !== "";

const withUnit = (value, unit) =>
  hasReadingValue(value) && unit ? `${value} ${unit}` : value;

const formatExportValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const csvEscape = (value) => {
  const text = formatExportValue(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const sanitizeFileName = (value) =>
  String(value || "additional-sensor-history")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const escapeHtml = (value) =>
  formatExportValue(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const pdfText = (value) =>
  formatExportValue(value)
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapePdfText = (value) =>
  pdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const toDateInputValue = (date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const getTodayDateString = () => toDateInputValue(new Date());

const getCurrentMonthStartDateString = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const addMonths = (dateString, months) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1 + months, day);
  if (date.getDate() !== day) date.setDate(0);
  return date;
};

const getDateValue = (dateString) => new Date(`${dateString}T00:00:00`);

const formatHistoryDateTime = (timestamp) => {
  const timestampMs = getTimestampMs(timestamp);
  if (!timestampMs) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: IST_TIME_ZONE,
  }).format(new Date(timestampMs));
};

const formatDateLabel = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(getDateValue(value));
};

const getSensorDisplayName = (sensor) =>
  String(sensor?.sensor_name || sensor?.s_id || "Sensor").trim();

const getSensorGridClassName = (sensorCount) => {
  if (sensorCount <= 1) return "grid-cols-1";
  if (sensorCount === 2) return "grid-cols-1 sm:grid-cols-2";
  return "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3";
};

const getSensorTileClassName = (sensorCount) => {
  if (sensorCount <= 1) return "min-h-[260px]";
  if (sensorCount === 2) return "min-h-[230px]";
  return "min-h-[178px]";
};

const getDeviceGroupLabel = (sensor) => {
  const label = sensor.device_type || "Other";
  if (label === "ATBC") return "ATBC / Pump Controller";
  if (label === "ATVC") return "ATVC / Level Controller";
  if (label === "ATBPC") return "ATBPC / Borewell Controller";
  return label;
};

const AdditionalSensors = () => {
  const mqttClientRef = useRef(null);
  const [availableSensors, setAvailableSensors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [readings, setReadings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [formState, setFormState] = useState({ name: "", sensor_ids: [] });
  const [activeTab, setActiveTab] = useState("cards");
  const [historyFilters, setHistoryFilters] = useState({
    sensorId: ALL_SENSORS_VALUE,
    startDate: getCurrentMonthStartDateString(),
    endDate: getTodayDateString(),
  });
  const [historyReport, setHistoryReport] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExporting, setHistoryExporting] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyChart, setHistoryChart] = useState(null);
  const [historyChartLoading, setHistoryChartLoading] = useState(false);
  const [historyChartInterval, setHistoryChartInterval] = useState(10);
  // Filters of the last Search, so changing the window re-plots the same query.
  const [historyChartQuery, setHistoryChartQuery] = useState(null);
  const historyChartRequestRef = useRef(0);

  const sensorById = useMemo(
    () =>
      availableSensors.reduce((map, sensor) => {
        map[sensor.id] = sensor;
        return map;
      }, {}),
    [availableSensors],
  );

  const sensorsByDeviceId = useMemo(
    () =>
      availableSensors.reduce((map, sensor) => {
        const deviceKey = normalizeDeviceId(sensor.device_id);
        const list = map.get(deviceKey) || [];
        list.push(sensor);
        map.set(deviceKey, list);
        return map;
      }, new Map()),
    [availableSensors],
  );

  const groupedSensors = useMemo(() => {
    return availableSensors.reduce((groupsMap, sensor) => {
      const groupLabel = getDeviceGroupLabel(sensor);
      if (!groupsMap[groupLabel]) groupsMap[groupLabel] = [];
      groupsMap[groupLabel].push(sensor);
      return groupsMap;
    }, {});
  }, [availableSensors]);

  const historySensorOptions = useMemo(
    () =>
      availableSensors
        .filter((sensor) => sensor.id)
        .map((sensor) => ({ value: sensor.id, label: getHistorySensorLabel(sensor) }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [availableSensors],
  );

  const selectedHistorySensorLabel =
    historyFilters.sensorId === ALL_SENSORS_VALUE
      ? "All sensors"
      : historySensorOptions.find(
          (option) => option.value === historyFilters.sensorId,
        )?.label || "-";

  const historyRows = historyReport?.rows || [];
  const historyPagination = historyReport?.pagination || {
    page: historyPage,
    page_size: 50,
    total_rows: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  };
  const historyColumns = useMemo(
    () => [
      {
        label: "S.No",
        getValue: (row) => row.serial_number,
      },
      {
        label: "Timestamp",
        getValue: (row) => formatHistoryDateTime(row.timestamp_ist || row.timestamp),
      },
      {
        label: "Sensor ID",
        getValue: (row) => row.sensor_id,
      },
      {
        label: "Reading",
        getValue: (row) => withUnit(row.value, row.unit),
      },
    ],
    [],
  );

  const loadData = useCallback(async (showToast = false) => {
    try {
      const [sensorsData, groupsData, readingsData] = await Promise.all([
        api.getAvailableAdditionalSensors(),
        api.getAdditionalSensorGroups(),
        api.getAdditionalSensorReadings(),
      ]);
      setAvailableSensors(sensorsData);
      setGroups(groupsData);
      setReadings(readingsData || {});
      if (showToast) toast.success("Additional sensors refreshed");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to load additional sensors"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyPacketReadings = useCallback(
    (packet) => {
      if (!packet?.device_id || !Array.isArray(packet.additional_sensors)) return;

      const configuredSensors =
        sensorsByDeviceId.get(normalizeDeviceId(packet.device_id)) || [];
      if (configuredSensors.length === 0) return;

      const packetSensorMap = packet.additional_sensors.reduce((map, item) => {
        map.set(normalizeSensorId(item?.id), item);
        return map;
      }, new Map());

      setReadings((current) => {
        let changed = false;
        const next = { ...current };

        configuredSensors.forEach((sensor) => {
          const matched = packetSensorMap.get(normalizeSensorId(sensor.s_id));
          if (!matched) return;

          next[sensor.id] = {
            value: matched.val,
            timestamp: packet.timestamp,
            device_id: packet.device_id,
            sensor_id: sensor.s_id,
          };
          changed = true;
        });

        return changed ? next : current;
      });
    },
    [sensorsByDeviceId],
  );

  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER_URL, {
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      reconnectPeriod: 2000,
      clean: true,
    });

    mqttClientRef.current = client;

    client.on("connect", () => {
      client.subscribe(
        [MQTT_D2C_TOPIC, ...getStateTopicSubscriptions()],
        (error) => {
        if (error) toast.error("Failed to subscribe to sensor updates");
        },
      );
    });

    client.on("message", (_topic, message) => {
      try {
        extractPackets(JSON.parse(message.toString())).forEach(applyPacketReadings);
      } catch (error) {
        console.error("Invalid MQTT sensor packet", error);
      }
    });

    client.on("reconnect", () => {});
    client.on("close", () => {});
    client.on("error", (error) => {
      console.error("MQTT sensor error:", error);
    });

    return () => {
      client.end(true);
      mqttClientRef.current = null;
    };
  }, [applyPacketReadings]);

  const resetForm = () => {
    setEditingGroupId(null);
    setFormState({ name: "", sensor_ids: [] });
  };

  const openCreateDialog = () => {
    resetForm();
    setFormDialogOpen(true);
  };

  const closeFormDialog = () => {
    resetForm();
    setFormDialogOpen(false);
  };

  const toggleSensor = (sensorId) => {
    setFormState((current) => {
      const exists = current.sensor_ids.includes(sensorId);
      if (!exists && current.sensor_ids.length >= 5) {
        toast.error("A sensor card can contain maximum 5 sensors");
        return current;
      }

      return {
        ...current,
        sensor_ids: exists
          ? current.sensor_ids.filter((id) => id !== sensorId)
          : [...current.sensor_ids, sensorId],
      };
    });
  };

  const submitGroup = async () => {
    const name = formState.name.trim();
    if (!name) {
      toast.error("Card name is required");
      return;
    }
    if (formState.sensor_ids.length === 0) {
      toast.error("Select at least one sensor");
      return;
    }
    if (formState.sensor_ids.length > 5) {
      toast.error("A sensor card can contain maximum 5 sensors");
      return;
    }

    setSaving(true);
    try {
      const payload = { name, sensor_ids: formState.sensor_ids };
      if (editingGroupId) {
        await api.updateAdditionalSensorGroup(editingGroupId, payload);
        toast.success("Sensor card updated");
      } else {
        await api.createAdditionalSensorGroup(payload);
        toast.success("Sensor card created");
      }
      resetForm();
      setFormDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to save sensor card"));
    } finally {
      setSaving(false);
    }
  };

  const editGroup = (group) => {
    setEditingGroupId(group.id);
    setFormState({
      name: group.name || "",
      sensor_ids: group.sensor_ids || [],
    });
    setFormDialogOpen(true);
  };

  const removeSensorFromGroup = async (group, sensorId) => {
    const nextSensorIds = (group.sensor_ids || []).filter((id) => id !== sensorId);
    if (nextSensorIds.length === 0) {
      toast.error("A sensor card must contain at least one sensor");
      return;
    }

    try {
      await api.updateAdditionalSensorGroup(group.id, {
        name: group.name,
        sensor_ids: nextSensorIds,
      });
      toast.success("Sensor removed from card");
      await loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to remove sensor"));
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await api.deleteAdditionalSensorGroup(groupId);
      toast.success("Sensor card deleted");
      if (editingGroupId === groupId) resetForm();
      await loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete sensor card"));
    }
  };

  const setHistoryFilter = (key, value) => {
    setHistoryFilters((current) => ({ ...current, [key]: value }));
    setHistoryPage(1);
    setHistoryReport(null);
  };

  const validateHistoryFilters = () => {
    const { startDate, endDate } = historyFilters;
    const today = getTodayDateString();

    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return false;
    }
    if (startDate > today || endDate > today) {
      toast.error("Future dates are not allowed");
      return false;
    }
    if (endDate < startDate) {
      toast.error("End date must be greater than or equal to start date");
      return false;
    }
    if (getDateValue(endDate) >= addMonths(startDate, 2)) {
      toast.error("Date range cannot exceed 2 months");
      return false;
    }

    return true;
  };

  const loadHistory = async (page = 1) => {
    if (!validateHistoryFilters()) return;

    setHistoryLoading(true);
    try {
      const response = await api.getAdditionalSensorHistory({
        startDate: historyFilters.startDate,
        endDate: historyFilters.endDate,
        sensorId: getSensorIdFromSelectValue(historyFilters.sensorId),
        page,
      });
      const resolvedPage = response?.pagination?.page || page;
      setHistoryPage(resolvedPage);
      setHistoryReport(response);
      if (!response?.rows?.length) {
        toast.info("No historical sensor readings found for this filter");
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to load historical sensor data"),
      );
      setHistoryReport(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadHistoryChart = async (query, intervalMinutes) => {
    const requestId = historyChartRequestRef.current + 1;
    historyChartRequestRef.current = requestId;
    setHistoryChartLoading(true);
    try {
      const response = await api.getAdditionalSensorHistoryChart({
        ...query,
        intervalMinutes,
      });
      if (historyChartRequestRef.current !== requestId) return;
      setHistoryChart(response);
    } catch (error) {
      if (historyChartRequestRef.current !== requestId) return;
      toast.error(getApiErrorMessage(error, "Failed to load sensor chart"));
      setHistoryChart(null);
    } finally {
      if (historyChartRequestRef.current === requestId) {
        setHistoryChartLoading(false);
      }
    }
  };

  const searchHistory = () => {
    if (!validateHistoryFilters()) return;
    const query = {
      startDate: historyFilters.startDate,
      endDate: historyFilters.endDate,
      sensorId: getSensorIdFromSelectValue(historyFilters.sensorId),
    };
    setHistoryChartQuery(query);
    loadHistory(1);
    loadHistoryChart(query, historyChartInterval);
  };

  const changeHistoryChartInterval = (intervalMinutes) => {
    if (intervalMinutes === historyChartInterval) return;
    setHistoryChartInterval(intervalMinutes);
    if (historyChartQuery) loadHistoryChart(historyChartQuery, intervalMinutes);
  };

  const getChartSensorLabel = (sensorId) =>
    sensorById[sensorId] ? getHistorySensorLabel(sensorById[sensorId]) : "Sensor";

  const goToHistoryPage = (page) => {
    if (page < 1 || page === historyPage || historyLoading) return;
    loadHistory(page);
  };

  const fetchHistoryExportReport = async () => {
    if (!validateHistoryFilters()) return null;

    setHistoryExporting(true);
    try {
      const response = await api.getAdditionalSensorHistory({
        startDate: historyFilters.startDate,
        endDate: historyFilters.endDate,
        sensorId: getSensorIdFromSelectValue(historyFilters.sensorId),
        page: 1,
        exportAll: true,
      });

      if (!response?.rows?.length) {
        toast.error("No historical sensor data is available to export");
        return null;
      }

      return response;
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to export historical sensor data"),
      );
      return null;
    } finally {
      setHistoryExporting(false);
    }
  };

  const getHistoryExportMetaRows = (report, rows) => [
    ["Report", "Historical Additional Sensor Data"],
    ["Society", report?.society_name || historyReport?.society_name || "-"],
    [
      "Sensor",
      selectedHistorySensorLabel,
    ],
    [
      "Date Range",
      `${formatDateLabel(historyFilters.startDate)} 12:00:00 am to ${formatDateLabel(
        historyFilters.endDate,
      )} 11:59:59 pm`,
    ],
    [
      "Rows Exported",
      rows.length,
    ],
    ["Downloaded At", formatIST(Date.now())],
  ];

  const getHistoryFileName = () =>
    sanitizeFileName(
      `additional-sensor-history-${selectedHistorySensorLabel}-${historyFilters.startDate}-to-${historyFilters.endDate}`,
    );

  const downloadHistoryCsv = async () => {
    const exportReport = await fetchHistoryExportReport();
    if (!exportReport) return;
    const exportRows = exportReport.rows || [];

    const rows = [
      ...getHistoryExportMetaRows(exportReport, exportRows),
      [],
      historyColumns.map((column) => column.label),
      ...exportRows.map((row) =>
        historyColumns.map((column) => column.getValue(row)),
      ),
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\r\n");
    downloadBlob(
      `\uFEFF${csv}`,
      `${getHistoryFileName()}.csv`,
      "text/csv;charset=utf-8;",
    );
  };

  const downloadHistoryExcel = async () => {
    const exportReport = await fetchHistoryExportReport();
    if (!exportReport) return;
    const exportRows = exportReport.rows || [];

    const metaRows = getHistoryExportMetaRows(exportReport, exportRows)
      .map(
        ([label, value]) =>
          `<tr><th style="text-align:left;background:#eaf6ff;">${escapeHtml(
            label,
          )}</th><td>${escapeHtml(value)}</td></tr>`,
      )
      .join("");
    const headerRow = historyColumns
      .map(
        (column) =>
          `<th style="text-align:left;background:#0f4c75;color:#ffffff;">${escapeHtml(
            column.label,
          )}</th>`,
      )
      .join("");
    const dataRows = exportRows
      .map(
        (row) =>
          `<tr>${historyColumns
            .map((column) => `<td>${escapeHtml(column.getValue(row))}</td>`)
            .join("")}</tr>`,
      )
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8" /></head><body><table border="1">${metaRows}<tr></tr><tr>${headerRow}</tr>${dataRows}</table></body></html>`;

    downloadBlob(
      html,
      `${getHistoryFileName()}.xls`,
      "application/vnd.ms-excel;charset=utf-8;",
    );
  };

  const downloadHistoryPdf = async () => {
    const exportReport = await fetchHistoryExportReport();
    if (!exportReport) return;
    const exportRows = exportReport.rows || [];

    const page = { width: 841.89, height: 595.28, margin: 28 };
    const columns = historyColumns;
    const columnWidths = [34, 104, 84, 128, 72, 92, 76, 188];
    const rowHeight = 18;
    const lines = [];
    let y = page.margin;
    const add = (command) => lines.push(command);
    const addText = (
      text,
      x,
      top,
      size = 8,
      font = "F1",
      color = "0.08 0.11 0.18",
    ) => {
      add(`${color} rg`);
      add(
        `BT /${font} ${size} Tf ${x.toFixed(2)} ${(page.height - top - size).toFixed(
          2,
        )} Td (${escapePdfText(text)}) Tj ET`,
      );
    };
    const addRect = (x, top, width, height, color) => {
      add(`${color} rg`);
      add(
        `${x.toFixed(2)} ${(page.height - top - height).toFixed(2)} ${width.toFixed(
          2,
        )} ${height.toFixed(2)} re f`,
      );
    };

    addRect(page.margin, y, page.width - page.margin * 2, 28, "0.90 0.97 1");
    addText("Historical Additional Sensor Data", page.margin + 10, y + 8, 14, "F2");
    y += 40;
    getHistoryExportMetaRows(exportReport, exportRows).forEach(([label, value]) => {
      addText(`${label}: ${value}`, page.margin, y, 8, "F2");
      y += 12;
    });
    y += 8;

    const drawHeader = () => {
      let x = page.margin;
      addRect(page.margin, y, page.width - page.margin * 2, rowHeight, "0.06 0.30 0.46");
      columns.forEach((column, index) => {
        addText(column.label, x + 3, y + 5, 7, "F2", "1 1 1");
        x += columnWidths[index];
      });
      y += rowHeight;
    };

    drawHeader();
    const pages = [];
    const flushPage = () => {
      pages.push([...lines]);
      lines.length = 0;
      y = page.margin;
      drawHeader();
    };

    exportRows.forEach((row, rowIndex) => {
      if (y + rowHeight > page.height - page.margin) flushPage();
      let x = page.margin;
      addRect(
        page.margin,
        y,
        page.width - page.margin * 2,
        rowHeight,
        rowIndex % 2 ? "0.96 0.98 1" : "1 1 1",
      );
      columns.forEach((column, index) => {
        addText(pdfText(column.getValue(row)).slice(0, 42), x + 3, y + 5, 7);
        x += columnWidths[index];
      });
      y += rowHeight;
    });
    pages.push([...lines]);

    const objects = [];
    const addObject = (content) => {
      objects.push(content);
      return objects.length;
    };
    const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
    const pagesId = addObject("");
    const fontId = addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
    const boldFontId = addObject(
      "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
    );
    const pageIds = pages.map((commands) => {
      const stream = commands.join("\n");
      const contentId = addObject(
        `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
      );
      return addObject(
        `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${page.width} ${page.height}] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> >> /Contents ${contentId} 0 R >>`,
      );
    });
    objects[pagesId - 1] =
      `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

    let pdf = "%PDF-1.4\n";
    const offsets = [];
    objects.forEach((object, index) => {
      offsets.push(pdf.length);
      pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    });
    const xrefOffset = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    offsets.forEach((offset) => {
      pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
    });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    downloadBlob(pdf, `${getHistoryFileName()}.pdf`, "application/pdf");
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">
              Additional Sensors
            </h1>
            <p className="text-slate-600">
              Build custom live cards from configured sensor definitions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => loadData(true)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
          <button
            type="button"
            onClick={() => setActiveTab("cards")}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
              activeTab === "cards"
                ? "bg-sky-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            <Activity className="h-4 w-4" />
            Sensor Cards
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("history")}
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition ${
              activeTab === "history"
                ? "bg-sky-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            <History className="h-4 w-4" />
            Historical Data
          </button>
        </div>

        {activeTab === "cards" ? (
          <>
        <Card>
          <CardContent className="flex flex-col gap-3 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                Sensor Cards
              </h2>
              <p className="text-sm text-slate-500">
                Create a custom live card from configured additional sensors.
              </p>
            </div>
            <Button onClick={openCreateDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Sensor Card
            </Button>
          </CardContent>
        </Card>

        <Dialog open={formDialogOpen} onOpenChange={(open) => {
          if (open) setFormDialogOpen(true);
          else closeFormDialog();
        }}>
          <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto p-0">
            <DialogHeader className="border-b px-6 py-5">
              <DialogTitle className="flex items-center gap-2 text-2xl">
                {editingGroupId ? (
                  <Edit2 className="h-5 w-5" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
                {editingGroupId ? "Edit Sensor Card" : "Create Sensor Card"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 px-6 pb-6 pt-5">
              <div className="grid gap-5 lg:grid-cols-[minmax(220px,320px)_1fr]">
                <div className="space-y-2">
                  <Label htmlFor="sensor-card-name">Card Name</Label>
                  <Input
                    id="sensor-card-name"
                    value={formState.name}
                    onChange={(event) =>
                      setFormState((current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    placeholder="Quality Sensors"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Select Sensors</Label>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    {availableSensors.length === 0 ? (
                      <p className="text-sm text-slate-500">
                        No additional sensors configured in Infrastructure.
                      </p>
                    ) : (
                      <div className="grid max-h-[52vh] gap-3 overflow-y-auto pr-1 md:grid-cols-2 xl:grid-cols-3">
                        {Object.entries(groupedSensors).map(([groupLabel, sensors]) => (
                          <div
                            key={groupLabel}
                            className="rounded-md border border-slate-200 bg-white p-3"
                          >
                            <p className="mb-2 text-xs font-bold uppercase text-slate-500">
                              {groupLabel}
                            </p>
                            <div className="space-y-2">
                              {sensors.map((sensor) => (
                                <label
                                  key={sensor.id}
                                  className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1 hover:bg-slate-50"
                                >
                                  <Checkbox
                                    checked={formState.sensor_ids.includes(sensor.id)}
                                    disabled={
                                      !formState.sensor_ids.includes(sensor.id) &&
                                      formState.sensor_ids.length >= 5
                                    }
                                    onCheckedChange={() => toggleSensor(sensor.id)}
                                  />
                                  <span className="min-w-0 text-sm">
                                    <span className="block font-semibold text-slate-800">
                                      {sensor.s_id}
                                    </span>
                                    <span className="block truncate text-xs text-slate-500">
                                      {getSensorDisplayName(sensor)} - {sensor.device_id}
                                    </span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
                <p className="text-sm text-slate-500">
                  {formState.sensor_ids.length} sensor
                  {formState.sensor_ids.length === 1 ? "" : "s"} selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeFormDialog}>
                    <X className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                  <Button onClick={submitGroup} disabled={saving}>
                    {saving ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {editingGroupId ? "Update Card" : "Create Card"}
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="flex min-h-[220px] items-center justify-center">
            <Loader className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : groups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Activity className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900">
                No sensor cards yet
              </h3>
              <p className="text-sm text-slate-500">
                Create a card and select only the sensors you want to monitor.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="mt-4 grid gap-4 xl:grid-cols-2">
            {groups.map((group) => {
              const selectedSensors = (group.sensor_ids || []).map(
                (sensorId) => ({
                  id: sensorId,
                  sensor: sensorById[sensorId],
                }),
              );

              return (
                <Card key={group.id} className="border-sky-100 shadow-sm">
                  <CardHeader className="flex flex-row items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-xl">{group.name}</CardTitle>
                      <p className="text-sm text-slate-500">
                        {selectedSensors.length} selected sensor
                        {selectedSensors.length === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => editGroup(group)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => deleteGroup(group.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="pb-6">
                    <div
                      className={`grid auto-rows-fr gap-4 ${getSensorGridClassName(
                        selectedSensors.length,
                      )}`}
                    >
                      {selectedSensors.map(({ id, sensor }) => {
                        const reading = readings[id];
                        return (
                          <div
                            key={id}
                            className={`relative flex flex-col rounded-lg border border-slate-200 bg-white p-4 text-center shadow-sm ${getSensorTileClassName(
                              selectedSensors.length,
                            )}`}
                          >
                            <div className="absolute -top-3 left-1/2 inline-flex max-w-[calc(100%-2rem)] -translate-x-1/2 overflow-hidden rounded-md border border-slate-200 bg-white text-xs font-semibold shadow-sm">
                              <span className="border-r border-slate-200 px-2 py-1 text-slate-800">
                                {sensor?.s_id || id}
                              </span>
                              <span className="truncate px-2 py-1 text-slate-500">
                                {sensor?.device_id || "Missing"}
                              </span>
                            </div>
                            <div className="mt-5 flex flex-1 flex-col items-center justify-center gap-2">
                              <div>
                                <p className="text-4xl font-bold text-slate-950">
                                  {formatValue(reading?.value)}
                                  {hasReadingValue(reading?.value) &&
                                    sensor?.unit && (
                                      <span className="ml-1.5 text-xl font-semibold text-slate-500">
                                        {sensor.unit}
                                      </span>
                                    )}
                                </p>
                                <p className="mt-1 text-xl font-semibold text-slate-800">
                                  {getSensorDisplayName(sensor)}
                                </p>
                              </div>
                              <div>
                                <p className="text-sm text-slate-500">
                                  {formatIST(reading?.timestamp)}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
            );
          })}
        </div>
        )}
          </>
        ) : (
          <div className="space-y-5">
            <Card>
              <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <CardTitle className="text-xl">Historical Sensor Data</CardTitle>
                  <p className="text-sm text-slate-500">
                    Search additional sensor readings by device and date range.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={downloadHistoryCsv}
                    disabled={historyLoading || historyExporting}
                  >
                    {historyExporting ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadHistoryExcel}
                    disabled={historyLoading || historyExporting}
                  >
                    {historyExporting ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                    )}
                    Excel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadHistoryPdf}
                    disabled={historyLoading || historyExporting}
                  >
                    {historyExporting ? (
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <FileText className="mr-2 h-4 w-4" />
                    )}
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr_1fr_auto]">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-slate-500" />
                      Sensor
                    </Label>
                    <Select
                      value={historyFilters.sensorId}
                      onValueChange={(value) =>
                        setHistoryFilter("sensorId", value)
                      }
                      disabled={loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sensor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_SENSORS_VALUE}>
                          All sensors
                        </SelectItem>
                        {historySensorOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="history-start-date"
                      className="flex items-center gap-2"
                    >
                      <Calendar className="h-4 w-4 text-slate-500" />
                      Start Date
                    </Label>
                    <Input
                      id="history-start-date"
                      type="date"
                      value={historyFilters.startDate}
                      max={getTodayDateString()}
                      onChange={(event) =>
                        setHistoryFilter("startDate", event.target.value)
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="history-end-date"
                      className="flex items-center gap-2"
                    >
                      <Calendar className="h-4 w-4 text-slate-500" />
                      End Date
                    </Label>
                    <Input
                      id="history-end-date"
                      type="date"
                      value={historyFilters.endDate}
                      max={getTodayDateString()}
                      onChange={(event) =>
                        setHistoryFilter("endDate", event.target.value)
                      }
                    />
                  </div>

                  <div className="flex items-end">
                    <Button
                      className="w-full bg-slate-900 hover:bg-slate-700 lg:w-auto"
                      onClick={searchHistory}
                      disabled={historyLoading || loading}
                    >
                      {historyLoading ? (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="mr-2 h-4 w-4" />
                      )}
                      Search
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Sensor Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <SensorHistoryChart
                  chart={historyChart}
                  loading={historyChartLoading}
                  hasSearched={Boolean(historyChartQuery)}
                  intervalMinutes={historyChartInterval}
                  onIntervalChange={changeHistoryChartInterval}
                  getSensorLabel={getChartSensorLabel}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Sensor Readings</CardTitle>
                <p className="text-sm text-slate-500">
                  {historyReport
                    ? `${historyPagination.total_rows} reading${
                        historyPagination.total_rows === 1 ? "" : "s"
                      } found. Showing ${historyRows.length} on this page.`
                    : "Run a search to view historical readings."}
                </p>
              </CardHeader>
              <CardContent>
                {!historyReport ? (
                  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center">
                    <History className="mx-auto mb-3 h-10 w-10 text-slate-400" />
                    <p className="font-medium text-slate-700">No data loaded yet</p>
                    <p className="mt-1 text-sm text-slate-500">
                      Choose filters and click Search.
                    </p>
                  </div>
                ) : historyRows.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-12 text-center">
                    <Activity className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                    <p className="font-medium text-slate-700">
                      No sensor readings found
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Try another device or date range.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-lg border border-slate-200">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            {historyColumns.map((column) => (
                              <th
                                key={column.label}
                                className="whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700"
                              >
                                {column.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {historyRows.map((row) => (
                            <tr
                              key={`${row.serial_number}-${row.timestamp_ist}-${row.device_id}-${row.sensor_id}`}
                              className="odd:bg-white even:bg-slate-50/70"
                            >
                              {historyColumns.map((column) => (
                                <td
                                  key={column.label}
                                  className="whitespace-nowrap px-4 py-3 text-slate-700"
                                >
                                  {formatExportValue(column.getValue(row))}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {historyReport && historyPagination.total_pages > 1 && (
                  <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-slate-500">
                      Page {historyPagination.page} of{" "}
                      {historyPagination.total_pages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => goToHistoryPage(historyPage - 1)}
                        disabled={
                          historyLoading || !historyPagination.has_previous
                        }
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => goToHistoryPage(historyPage + 1)}
                        disabled={historyLoading || !historyPagination.has_next}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdditionalSensors;
